/**
 * The reward shop — what coins are actually for.
 *
 * Everything purchasable is cosmetic. Nothing here buys progress, skips a
 * lesson, or gives a scoring advantage, which keeps the incentive pointed at
 * practice rather than at the shop. Prices are tuned so a child who practises
 * for about a week can afford their first meaningful unlock.
 */
import type { ShopItem } from '../types';

export const SHOP_ITEMS: ShopItem[] = [
  /* ── avatar: hair ────────────────────────────────────────────────────── */
  {
    id: 'hair-curly',
    category: 'avatar',
    name: 'Curly Hair',
    description: 'Big, bouncy curls.',
    cost: 30,
    emoji: '🌀',
    value: 'curly',
  },
  {
    id: 'hair-long',
    category: 'avatar',
    name: 'Long Hair',
    description: 'Flowing past the shoulders.',
    cost: 30,
    emoji: '💇',
    value: 'long',
  },
  {
    id: 'hair-buns',
    category: 'avatar',
    name: 'Space Buns',
    description: 'Two neat buns.',
    cost: 45,
    emoji: '👧',
    value: 'buns',
  },
  {
    id: 'hair-spiky',
    category: 'avatar',
    name: 'Spiky Hair',
    description: 'Straight up, always.',
    cost: 45,
    emoji: '⚡',
    value: 'spiky',
  },

  /* ── avatar: accessories ─────────────────────────────────────────────── */
  {
    id: 'acc-glasses',
    category: 'avatar',
    name: 'Round Glasses',
    description: 'Very studious.',
    cost: 40,
    emoji: '👓',
    value: 'glasses',
  },
  {
    id: 'acc-cap',
    category: 'avatar',
    name: 'Baseball Cap',
    description: 'Worn forwards, obviously.',
    cost: 55,
    emoji: '🧢',
    value: 'cap',
  },
  {
    id: 'acc-headphones',
    category: 'avatar',
    name: 'Headphones',
    description: 'For focus mode.',
    cost: 70,
    emoji: '🎧',
    value: 'headphones',
  },
  {
    id: 'acc-crown',
    category: 'avatar',
    name: 'Golden Crown',
    description: 'For Master Typists only.',
    cost: 250,
    emoji: '👑',
    requiresRank: 8,
    value: 'crown',
  },

  /* ── avatar: backgrounds ─────────────────────────────────────────────── */
  {
    id: 'bg-sunset',
    category: 'avatar',
    name: 'Sunset',
    description: 'Warm orange sky.',
    cost: 35,
    emoji: '🌇',
    value: 'sunset',
  },
  {
    id: 'bg-space',
    category: 'avatar',
    name: 'Outer Space',
    description: 'Stars and a distant planet.',
    cost: 60,
    emoji: '🌌',
    value: 'space',
  },
  {
    id: 'bg-ocean',
    category: 'avatar',
    name: 'Deep Ocean',
    description: 'Cool blue water.',
    cost: 60,
    emoji: '🌊',
    value: 'ocean',
  },
  {
    id: 'bg-forest',
    category: 'avatar',
    name: 'Forest',
    description: 'Green and quiet.',
    cost: 60,
    emoji: '🌲',
    value: 'forest',
  },

  /* ── pets ────────────────────────────────────────────────────────────── */
  {
    id: 'pet-cat',
    category: 'pet',
    name: 'Pepper the Cat',
    description: 'Sits beside you while you practise.',
    cost: 80,
    emoji: '🐈',
    value: 'cat',
  },
  {
    id: 'pet-dog',
    category: 'pet',
    name: 'Scout the Dog',
    description: 'Wags every time you get a star.',
    cost: 80,
    emoji: '🐕',
    value: 'dog',
  },
  {
    id: 'pet-dragon',
    category: 'pet',
    name: 'Ember the Dragon',
    description: 'Small, friendly, slightly smoky.',
    cost: 200,
    emoji: '🐉',
    requiresRank: 6,
    value: 'dragon',
  },
  {
    id: 'pet-robot',
    category: 'pet',
    name: 'Bolt the Robot',
    description: 'From the Level 13 story.',
    cost: 150,
    emoji: '🤖',
    requiresRank: 5,
    value: 'robot',
  },

  /* ── keyboard skins ──────────────────────────────────────────────────── */
  {
    id: 'kb-classic',
    category: 'keyboard',
    name: 'Classic',
    description: 'Clean and simple.',
    cost: 0,
    emoji: '⌨️',
    value: 'classic',
  },
  {
    id: 'kb-candy',
    category: 'keyboard',
    name: 'Candy Keys',
    description: 'Pastel keycaps.',
    cost: 50,
    emoji: '🍬',
    value: 'candy',
  },
  {
    id: 'kb-neon',
    category: 'keyboard',
    name: 'Neon Night',
    description: 'Glowing edges in the dark.',
    cost: 90,
    emoji: '💡',
    value: 'neon',
  },
  {
    id: 'kb-wood',
    category: 'keyboard',
    name: 'Woodgrain',
    description: 'Warm and quiet.',
    cost: 90,
    emoji: '🪵',
    value: 'wood',
  },
  {
    id: 'kb-gold',
    category: 'keyboard',
    name: 'Solid Gold',
    description: 'Absurd. Wonderful.',
    cost: 220,
    emoji: '🥇',
    requiresRank: 7,
    value: 'gold',
  },

  /* ── titles ──────────────────────────────────────────────────────────── */
  {
    id: 'title-explorer',
    category: 'title',
    name: '"the Explorer"',
    description: 'A title beside your name.',
    cost: 40,
    emoji: '🧭',
    value: 'the Explorer',
  },
  {
    id: 'title-swift',
    category: 'title',
    name: '"the Swift"',
    description: 'A title beside your name.',
    cost: 90,
    emoji: '💨',
    value: 'the Swift',
  },
  {
    id: 'title-fearless',
    category: 'title',
    name: '"the Fearless"',
    description: 'A title beside your name.',
    cost: 90,
    emoji: '🦁',
    value: 'the Fearless',
  },
  {
    id: 'title-legend',
    category: 'title',
    name: '"the Legend"',
    description: 'Only for the very best.',
    cost: 300,
    emoji: '🏆',
    requiresRank: 9,
    value: 'the Legend',
  },
];

export const SHOP_CATEGORIES: { id: ShopItem['category']; label: string; emoji: string }[] = [
  { id: 'avatar', label: 'Looks', emoji: '🧑' },
  { id: 'pet', label: 'Pets', emoji: '🐾' },
  { id: 'keyboard', label: 'Keyboards', emoji: '⌨️' },
  { id: 'title', label: 'Titles', emoji: '🏷️' },
];

const BY_ID = new Map(SHOP_ITEMS.map((i) => [i.id, i]));
export const getShopItem = (id: string) => BY_ID.get(id) ?? null;

/**
 * The daily treasure chest. Rewards scale with the current streak, so showing
 * up two days running is visibly better than showing up twice in a month.
 */
export function chestReward(streak: number): { coins: number; label: string; emoji: string } {
  if (streak >= 30) return { coins: 60, label: 'Legendary chest', emoji: '💎' };
  if (streak >= 14) return { coins: 40, label: 'Golden chest', emoji: '🏆' };
  if (streak >= 7) return { coins: 28, label: 'Silver chest', emoji: '🥈' };
  if (streak >= 3) return { coins: 18, label: 'Bronze chest', emoji: '🥉' };
  return { coins: 10, label: 'Wooden chest', emoji: '🎁' };
}
